<template lang="pug">
.resident-in-out-schedule-list-tab
  .mb-3
  ResidentInOutScheduleTable(v-model="items" :fields="fields" :totalRows.sync="rows" :currentPage="currentPage" :perPage="perPage"
    :isLoading="isLoading" :filter="filter" :filterFields="filterFields" @onEdit="onEdit")
  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    // フィルタリング
    span.table-option-text 利用者グループ:
    .filter
      b-form-select(id="filter-input" v-model="filter" :options="filterOptions" size="md")
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(id="per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
    // ページネーション
    b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" @change="selectPage" hide-goto-end-buttons)
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import RoomsService, { RoomGetModel } from "@/commons/services/Rooms";
import SystemError from "@/commons/models/SystemError";

import ResidentInOutScheduleTable, {
  ResidentInOutScheduleTableField,
  ResidentInOutScheduleTableItem,
  ResidentInOutScheduleType
} from "@/pc/containers/roomSchedules/ResidentInOutScheduleTable.vue";

/**
 * ### 仮押さえ／転床一覧タブコンポーネント
 */
@Component({
  components: {
    ResidentInOutScheduleTable
  }
})
export default class ResidentInOutScheduleListTab extends Vue {
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;

  /** グループIDとグループ名のMAP */
  groupNameMap: Map<number, string> = new Map();
  /** 部屋IDと部屋のMAP */
  roomMap: Map<number, RoomGetModel> = new Map();

  /** 列名 */
  fields: Array<ResidentInOutScheduleTableField> = [];
  /** 各行の情報 */
  items: Array<ResidentInOutScheduleTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["groupId"];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.residentInOutSchedule.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.residentInOutSchedule.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.residentInOutSchedule.pageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `resident-in-out-schedule-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.items = [];
    this.currentRows = this.items.length;
    this.currentPage = 1; // 削除による画面の再描画もあるので、現在ページ番号はここで初期化する

    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<ResidentInOutScheduleTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.resident.perPage);

    // グループIDとグループ名の変換用にグループ情報のMAPを作る
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupNameMap: Map<number, string> = this.createGroupNameMap(groups);
    this.groupNameMap = groupNameMap;

    // 部屋用途、部屋種別の変換用に部屋情報のMAPを作る
    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomMap: Map<number, RoomGetModel> = this.createRoomMap(rooms);
    this.roomMap = roomMap;

    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<ResidentInOutScheduleTableField> {
    let fields: Array<ResidentInOutScheduleTableField> = [];

    fields.push({ key: "type", label: "種別", sortable: false, sortDirection: "desc", class: "type text-center" });
    fields.push({ key: "roomUseType", label: "部屋用途", sortable: false, sortDirection: "desc", class: "room-use-type" });
    fields.push({ key: "inOutDatetime", label: "入退所日時", sortable: false, sortDirection: "desc", class: "in-out-datetime" });
    fields.push({ key: "pickUpDropOffTime", label: "送迎", sortable: false, sortDirection: "desc", class: "pick-up-drop-off-time" });
    fields.push({ key: "groupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "group-name" });
    fields.push({ key: "residentName", label: "利用者名（部屋名）", sortable: false, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["resident-in-out-schedule-list"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループIDとグループ名のMAPを作成する
   */
  public createGroupNameMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, string> {
    let groupMap: Map<number, string> = new Map<number, string>();
    for (const group of residentGroups) {
      groupMap.set(group.id, group.name);
    }
    return groupMap;
  }

  /**
   * ### 部屋を取得する
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    let response: Array<RoomGetModel> = [];

    try {
      response = await this.roomsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.roomsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.roomsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 部屋IDと部屋MAPを作成する
   */
  public createRoomMap(rooms: Array<RoomGetModel>): Map<number, RoomGetModel> {
    let map: Map<number, RoomGetModel> = new Map<number, RoomGetModel>();
    for (const room of rooms) {
      map.set(room.id, room);
    }
    return map;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // テーブルに渡す各プロパティを作成する
    const residents: Array<ResidentGetModel> = await this.getResidents();
    const items: Array<ResidentInOutScheduleTableItem> = this.createItems(residents, this.roomMap);
    const sortedItems: Array<ResidentInOutScheduleTableItem> = this.sortItems(items);
    this.items = this.items.concat(sortedItems);
    this.currentRows = this.items.length;
    this.rows = items.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### 利用者を作成する
   */
  public async getResidents(): Promise<Array<ResidentGetModel>> {
    let response: Array<ResidentGetModel> = [];

    try {
      response = await this.residentsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.residentsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.residentsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 行情報を作成する
   *
   * - 渡された利用者情報の入所日、退所日をもとに行情報を作成する
   * - 入所日が存在し、現在日時よりも未来の日時であれば行情報とする
   *   - 更に送迎時間が指定されていれば行情報に追加する
   * - 退所日が存在し、現在日時よりも未来の日時であれば行情報とする
   *   - 更に送迎時間が指定されていれば行情報に追加する
   */
  public createItems(residents: Array<ResidentGetModel>, roomMap: Map<number, RoomGetModel>): Array<ResidentInOutScheduleTableItem> {
    let items: Array<ResidentInOutScheduleTableItem> = [];

    const today: number = Number(this.$dateUtil.convertDateToString(new Date(), "yyyymmdd000000000")); // 比較用の現在日時

    for (const resident of residents) {
      // v3.29.0からv3.30.0にアップデートしたタイミングでキャッシュクリアしていない施設でinAndOutDatetimesがundefinedになる可能性がある
      // undefinedの場合はスキップする
      if (!resident.inAndOutDatetimes) {
        continue;
      }
      for (const inAndOutDatetime of resident.inAndOutDatetimes) {
        // 入所日として行情報を作成する
        if (inAndOutDatetime.inDatetime != null && today <= inAndOutDatetime.inDatetime) {
          let pickUpDropOffTime: string = "無し";
          if (inAndOutDatetime.pickUpDatetime != null) {
            pickUpDropOffTime = this.$dateUtil.convertNumberToString(inAndOutDatetime.pickUpDatetime, "hh:MM");
          }

          // 入所・退所情報の部屋情報を使用する
          const room = roomMap.get(inAndOutDatetime.roomId ? inAndOutDatetime.roomId : resident.roomId);
          items.push({
            ...resident,
            roomId: inAndOutDatetime.roomId ? inAndOutDatetime.roomId : resident.roomId,
            roomType: room ? room.roomType : resident.roomType,
            roomUseType: room ? room.useType : resident.roomUseType,
            type: ResidentInOutScheduleType.IN,
            inOutDatetime: inAndOutDatetime.inDatetime,
            pickUpDropOffTime: pickUpDropOffTime
          });
        }
        // 退所日をとして行情報を作成する
        if (inAndOutDatetime.outDatetime != null && today <= inAndOutDatetime.outDatetime) {
          let pickUpDropOffTime: string = "無し";
          if (inAndOutDatetime.dropOffDatetime != null) {
            pickUpDropOffTime = this.$dateUtil.convertNumberToString(inAndOutDatetime.dropOffDatetime, "hh:MM");
          }

          // 入所・退所情報の部屋情報を使用する
          const room = roomMap.get(inAndOutDatetime.roomId ? inAndOutDatetime.roomId : resident.roomId);
          items.push({
            ...resident,
            roomId: inAndOutDatetime.roomId ? inAndOutDatetime.roomId : resident.roomId,
            roomType: room ? room.roomType : resident.roomType,
            roomUseType: room ? room.useType : resident.roomUseType,
            type: ResidentInOutScheduleType.OUT,
            inOutDatetime: inAndOutDatetime.outDatetime,
            pickUpDropOffTime: pickUpDropOffTime
          });
        }
      }
    }

    return items;
  }

  /**
   * ### 行情報をソートする
   *
   * - 入退所日の昇順でソートする
   */
  public sortItems(items: Array<ResidentInOutScheduleTableItem>): Array<ResidentInOutScheduleTableItem> {
    const sortedItems: Array<ResidentInOutScheduleTableItem> = items.concat().sort((a, b) => {
      if (a.inOutDatetime < b.inOutDatetime) {
        return -1;
      }
      if (a.inOutDatetime > b.inOutDatetime) {
        return 1;
      }
      return 0;
    });

    return sortedItems;
  }

  /**
   * ### 親コンポーネントに修正ボタン押下を通知する
   */
  public onEdit(item: ResidentInOutScheduleTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   *
   * - 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
.resident-in-out-schedule-list-tab {
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}
</style>
